import { useEffect, useState } from "react";
import Lenis from "lenis";
import "@/App.css";
import Seo from "@/components/Seo";
import { STR } from "@/i18n";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Premio from "@/components/Premio";
import Manifesto from "@/components/Manifesto";
import Pistacchio from "@/components/Pistacchio";
import Gusti from "@/components/Gusti";
import Solidarieta from "@/components/Solidarieta";
import Cinema from "@/components/Cinema";
import Stores from "@/components/Stores";
import News from "@/components/News";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildJsonLd = (stores) =>
  stores.map((s) => ({
    "@context": "https://schema.org",
    "@type": "IceCreamShop",
    name: `Fiordipanna ${s.city}`,
    address: {
      "@type": "PostalAddress",
      streetAddress: s.address,
      addressLocality: s.city,
      addressCountry: "IT",
    },
    telephone: s.phone_tel,
    openingHours: `Tu-Su ${String(s.opens_at).padStart(2, "0")}:00-22:00`,
  }));

const translate = (c, lang) => {
  if (lang !== "en") return c;
  return {
    ...c,
    marquee: c.marquee_en || c.marquee,
    chapters: c.chapters.map((ch) => ({
      ...ch,
      tag: ch.tag_en || ch.tag,
      title: ch.title_en || ch.title,
      text: ch.text_en || ch.text,
    })),
    gusti: c.gusti.map((g) => ({ ...g, desc: g.desc_en || g.desc })),
    pistacchio: {
      ...c.pistacchio,
      tag: c.pistacchio.tag_en || c.pistacchio.tag,
      title: c.pistacchio.title_en || c.pistacchio.title,
      text: c.pistacchio.text_en || c.pistacchio.text,
      quote: c.pistacchio.quote_en || c.pistacchio.quote,
      facts: c.pistacchio.facts_en || c.pistacchio.facts,
      source_label: c.pistacchio.source_label_en || c.pistacchio.source_label,
    },
    solidarieta: {
      ...c.solidarieta,
      tag: c.solidarieta.tag_en || c.solidarieta.tag,
      title: c.solidarieta.title_en || c.solidarieta.title,
      text: c.solidarieta.text_en || c.solidarieta.text,
      link_label: c.solidarieta.link_label_en || c.solidarieta.link_label,
    },
    news: (c.news || []).map((n) => ({
      ...n,
      date: n.date_en || n.date,
      tag: n.tag_en || n.tag,
      title: n.title_en || n.title,
      text: n.text_en || n.text,
    })),
    stores: c.stores.map((s) => ({
      ...s,
      badge: s.badge_en || s.badge,
      hours: s.hours_en || s.hours,
    })),
  };
};

const SEO = {
  it: {
    title: "Fiordipanna — Gelateria Artigianale dal 1994 | Cornaredo, Arese, Pero",
    description:
      "Etica Artigiana dal 1994: gelato artigianale e produzione 100% senza glutine. Trova il negozio più vicino tra Cornaredo, Arese e Pero: orari, telefono, mappa e consegna a domicilio con Glovo e Deliveroo.",
  },
  en: {
    title: "Fiordipanna — Artisan Gelateria since 1994 | Cornaredo, Arese, Pero",
    description:
      "Etica Artigiana since 1994: artisan gelato with 100% gluten-free production. Find your nearest shop in Cornaredo, Arese and Pero: hours, phone, map and home delivery with Glovo and Deliveroo.",
  },
};

export default function App() {
  const [content, setContent] = useState(null);
  const [error, setError] = useState(false);
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem("fdp-lang");
      if (saved === "it" || saved === "en") return saved;
    } catch (e) {}
    return (navigator.language || "it").toLowerCase().startsWith("en") ? "en" : "it";
  });

  useEffect(() => {
    const lenis = new Lenis({ autoRaf: true, lerp: 0.09, anchors: { offset: -70 } });
    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("fdp-lang", lang);
    } catch (e) {}
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    fetch(`${API}/content`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("api"))))
      .then(setContent)
      .catch(() => setError(true));
  }, []);

  const t = STR[lang];

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#FAF7F2] text-[#241C1A]">
        <p data-testid="content-error" className="font-display text-2xl">{t.error}</p>
        <button
          data-testid="content-retry-button"
          onClick={() => window.location.reload()}
          className="rounded-full bg-[#C86D51] px-6 py-3 text-sm font-medium text-[#FAF7F2]"
        >
          {t.retry}
        </button>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]" data-testid="loading-screen">
        <img src="/images/logo-fiordipanna.png" alt="Fiordipanna" className="w-48 animate-pulse" />
      </div>
    );
  }

  const c = translate(content, lang);

  return (
    <div className="App min-h-screen bg-[#FAF7F2] text-[#241C1A] antialiased overflow-x-clip">
      <Seo
        title={SEO[lang].title}
        siteName="Fiordipanna"
        description={SEO[lang].description}
        image="/images/brand-hero.jpg"
        jsonLd={buildJsonLd(c.stores)}
      />
      <div className="grain-overlay" aria-hidden="true" />
      <Header t={t} lang={lang} setLang={setLang} />
      <main>
        <Hero t={t} />
        <Marquee items={c.marquee} />
        <Premio t={t} />
        <Manifesto chapters={c.chapters} t={t} />
        <Pistacchio data={c.pistacchio} />
        <Gusti gusti={c.gusti} t={t} />
        <Solidarieta data={c.solidarieta} />
        <Cinema t={t} />
        <Stores stores={c.stores} t={t} />
        <News items={c.news} t={t} />
      </main>
      <Footer contacts={c.contacts} stores={c.stores} t={t} />
    </div>
  );
}
